import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import SupportIcon from "@mui/icons-material/Support";
import EventIcon from "@mui/icons-material/Event";
import CancelIcon from "@mui/icons-material/Cancel";
import CircularProgress from "@mui/material/CircularProgress";
import { API_BASE_URL } from '../../../../../services/config.js';
import { AuthContext } from 'context';

function SaasDetail() {
  const { serviceId } = useParams();
  const { user } = useContext(AuthContext);
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/saas-service/${serviceId}`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Service-Details: ${response.statusText}`);
        }

        const data = await response.json();
        setService(data);
      } catch (err) {
        setError(err.message);
        console.error("Fehler beim Laden der Service-Details:", err);
      } finally {
        setLoading(false);
      }
    };

    if (serviceId) {
      fetchServiceDetails();
    }
  }, [serviceId]);

  const handleOpenServicePortal = () => {
    // URL basierend auf den tatsächlichen Daten des Services
    const serviceUrl = service?.serviceUrl || `https://portal.${service?.producttype || 'software'}.example.com/${serviceId}`;
    window.open(serviceUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenDocumentation = () => {
    // URL zur Dokumentation
    let docUrl = "https://docs.example.com";
    
    if (service?.producttype) {
      switch (service.producttype) {
        case "backup-now":
          docUrl = "https://docs.example.com/backup-now";
          break;
        case "office-365":
          docUrl = "https://docs.example.com/office-365";
          break;
        case "tsplus":
          docUrl = "https://docs.example.com/tsplus";
          break;
        case "eset":
          docUrl = "https://docs.example.com/eset";
          break;
        case "lastpass":
          docUrl = "https://docs.example.com/lastpass";
          break;
        case "nextcloud":
          docUrl = "https://docs.example.com/nextcloud";
          break;
        default:
          docUrl = `https://docs.example.com/saas/${service.producttype}`;
      }
    }
    
    window.open(docUrl, "_blank", "noopener,noreferrer");
  };

  const handleCreateSupportTicket = () => {
    // Navigation zum Support-Ticketsystem mit vorausgefüllten Informationen
    window.open(`/Support?serviceId=${serviceId}&type=${service?.producttype || 'saas'}`, "_blank", "noopener,noreferrer");
  };

  const handleBookAppointment = () => {
    // URL zum Terminbuchungssystem
    window.open("https://outlook.office365.com/owa/calendar/IhreEinrichtungdurchUDSSolutions@uds-solutions.de/bookings/", "_blank", "noopener,noreferrer");
  };
  
  const handleCancelService = () => {
    // URL zum Kündigungsformular
    window.open("https://forms.office.com/e/BF6w5pUKvq", "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress color="info" />
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox my={3} textAlign="center">
          <SoftTypography variant="h5" color="error">
            {error}
          </SoftTypography>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container spacing={3}>
          {/* Service Informationen */}
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <SoftBox display="flex" flexDirection="column" height="100%">
                      <SoftBox display="flex" alignItems="center" mb={1}>
                        <SubscriptionsIcon fontSize="large" color="info" sx={{ mr: 1 }} />
                        <SoftTypography variant="h5" fontWeight="bold">
                          {service?.servicename || 'SaaS Service'}
                        </SoftTypography>
                      </SoftBox>
                      
                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          ID:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular">
                          {service?.id || serviceId}
                        </SoftTypography>
                      </SoftBox>
                      
                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Produkttyp:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular">
                          {service?.producttype || 'Unbekannt'}
                        </SoftTypography>
                      </SoftBox>
                      
                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Status:
                        </SoftTypography>{" "}
                        <SoftTypography variant="button" fontWeight="regular" color={service?.sstatus === "active" ? "success" : service?.sstatus === "pending" ? "warning" : "error"}>
                          {service?.sstatus === "active" ? "Aktiv" : service?.sstatus === "pending" ? "Ausstehend" : service?.sstatus === "inactive" ? "Inaktiv" : service?.sstatus || "Unbekannt"}
                        </SoftTypography>
                      </SoftBox>
                      
                      {service?.hostname && (
                        <SoftBox mb={1}>
                          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                            Hostname:
                          </SoftTypography>{" "}
                          <SoftTypography variant="button" fontWeight="regular">
                            {service.hostname}
                          </SoftTypography>
                        </SoftBox>
                      )}
                    </SoftBox>
                  </Grid>
                  
                  <Grid item xs={12} lg={6}>
                    <SoftBox display="flex" flexDirection="column" height="100%">
                      <SoftTypography variant="h6" fontWeight="medium" mb={2}>
                        Lizenz- & Nutzungsinformationen
                      </SoftTypography>
                      
                      {service?.licenses && (
                        <SoftBox mb={1}>
                          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                            Anzahl Lizenzen:
                          </SoftTypography>{" "}
                          <SoftTypography variant="button" fontWeight="regular">
                            {service.licenses}
                          </SoftTypography>
                        </SoftBox>
                      )}
                      
                      {service?.storage && (
                        <SoftBox mb={1}>
                          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                            Speicher:
                          </SoftTypography>{" "}
                          <SoftTypography variant="button" fontWeight="regular">
                            {service.storage} GB
                          </SoftTypography>
                        </SoftBox>
                      )}
                      
                      {service?.features && (
                        <SoftBox mb={1}>
                          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                            Features:
                          </SoftTypography>{" "}
                          <SoftTypography variant="button" fontWeight="regular">
                            {typeof service.features === 'string' 
                              ? service.features
                              : Object.entries(service.features)
                                  .filter(([key, value]) => value)
                                  .map(([key]) => key)
                                  .join(', ')}
                          </SoftTypography>
                        </SoftBox>
                      )}
                      
                      <SoftBox mb={1}>
                        <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                          Monatlicher Preis:
                        </SoftTypography>{" "}
                        {service?.endcustomer_price && user?.id !== service?.partnerId ? (
                          <SoftTypography variant="button" fontWeight="regular">
                            {parseFloat(service.endcustomer_price).toFixed(2)} €
                          </SoftTypography>
                        ) : service?.configuration?.priceInfo?.discountApplied ? (
                          <div>
                            <SoftTypography variant="button" fontWeight="regular" style={{ textDecoration: 'line-through', color: '#666', marginRight: '10px' }}>
                              {service?.configuration?.priceInfo?.originalPrice.toFixed(2)} €
                            </SoftTypography>
                            <SoftTypography variant="button" fontWeight="bold" style={{ color: '#8b0000' }}>
                              {service?.sprice || '0.00'} € (20% Partner-Rabatt)
                            </SoftTypography>
                          </div>
                        ) : (
                          <SoftTypography variant="button" fontWeight="regular">
                            {service?.sprice || '0.00'} €
                          </SoftTypography>
                        )}
                      </SoftBox>
                      
                      {service?.verlaengerungszeitraum && (
                        <SoftBox mb={1}>
                          <SoftTypography variant="button" fontWeight="bold" textTransform="uppercase" color="text">
                            Verlängerungszeitraum:
                          </SoftTypography>{" "}
                          <SoftTypography variant="button" fontWeight="regular">
                            {service.verlaengerungszeitraum} {service.verlaengerungszeitraum === 1 ? 'Monat' : 'Monate'}
                          </SoftTypography>
                        </SoftBox>
                      )}
                      
                      <SoftBox mt="auto" pt={3}>
                        <Stack spacing={1} direction="row">
                          <SoftButton 
                            variant="gradient" 
                            color="info" 
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenServicePortal}
                          >
                            Service öffnen
                          </SoftButton>
                          <SoftButton 
                            variant="outlined" 
                            color="info" 
                            startIcon={<DescriptionIcon />}
                            onClick={handleOpenDocumentation}
                          >
                            Dokumentation
                          </SoftButton>
                          <SoftButton 
                            variant="outlined" 
                            color="dark" 
                            startIcon={<SupportIcon />}
                            onClick={handleCreateSupportTicket}
                          >
                            Support
                          </SoftButton>
                        </Stack>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>

          {/* Managed Service Information */}
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="medium" mb={1}>
                        Managed Service
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Dieser SaaS-Service wird von unserem Team vollständig betreut. Wir kümmern uns um die Lizenzverwaltung, Support und stehen Ihnen bei Fragen zur Verfügung.
                      </SoftTypography>
                      <SoftBox mt={2}>
                        <SoftTypography variant="body2" color="text">
                          Um die optimale Nutzung Ihres SaaS-Produkts sicherzustellen, empfehlen wir einen Einrichtungstermin mit unserem Team zu buchen. In diesem Gespräch können wir alle Funktionen erklären und die Software nach Ihren individuellen Anforderungen einrichten.
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }}}>
                    <SoftButton 
                      variant="gradient" 
                      color="success" 
                      startIcon={<EventIcon />}
                      onClick={handleBookAppointment}
                    >
                      Einrichtungstermin buchen
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>

          {/* Service Kündigen */}
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <SoftBox>
                      <SoftTypography variant="h6" fontWeight="medium" mb={1} color="error">
                        Service Kündigen
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Wenn Sie diesen SaaS-Service kündigen möchten, beachten Sie bitte folgende Konsequenzen:
                      </SoftTypography>
                      <SoftBox component="ul" pl={2} mt={1} mb={1}>
                        <SoftTypography component="li" variant="body2" color="text">
                          Alle Lizenzen werden zum Ende der Laufzeit deaktiviert.
                        </SoftTypography>
                        <SoftTypography component="li" variant="body2" color="text">
                          Gespeicherte Daten und Einstellungen werden nach Ablauf der Kündigungsfrist gelöscht.
                        </SoftTypography>
                        <SoftTypography component="li" variant="body2" color="text">
                          Die Kündigung wird zum Ende des aktuellen Abrechnungszeitraums wirksam.
                        </SoftTypography>
                        <SoftTypography component="li" variant="body2" color="text">
                          Stellen Sie sicher, dass Sie alle wichtigen Daten vor der Kündigung exportiert haben.
                        </SoftTypography>
                      </SoftBox>
                      <SoftTypography variant="body2" color="text" fontWeight="medium">
                        Ablauf der Kündigung:
                      </SoftTypography>
                      <SoftTypography variant="body2" color="text">
                        Wir prüfen die Anfrage und geben per Mail die Rückmeldung.
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }}}>
                    <SoftButton 
                      variant="gradient" 
                      color="error" 
                      startIcon={<CancelIcon />}
                      onClick={handleCancelService}
                    >
                      Service kündigen
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SaasDetail;